/**
 * Utility to translate nomenclature values displayed in the UI.
 * Nomenclature items from the database (task statuses, room types, etc.) are stored
 * in English. This utility looks up the Romanian translation when the user's
 * language is Romanian.
 *
 * Usage: translateNomenclature(t, 'task_statuses', item) where item has { code, name }
 * Returns: translated name, or original name if no translation exists (e.g. user-added items)
 */

/**
 * Normalize a key for lookup (spaces to underscores for nested keys that might have issues)
 */
function normalizeKey(str) {
  if (!str || typeof str !== 'string') return '';
  return str.trim();
}

/**
 * Translate a nomenclature item's display name.
 * @param {Function} t - i18next t function (from useTranslation)
 * @param {string} type - Nomenclature type (e.g. 'task_statuses', 'room_types', 'protocol_categories')
 * @param {Object} item - Item with code and/or name
 * @param {string} [item.code] - Optional code (e.g. 'HYG', 'ICU')
 * @param {string} [item.name] - Display name (e.g. 'Draft', 'Completed')
 * @returns {string} Translated name or original name as fallback
 */
export function translateNomenclature(t, type, item) {
  if (!item) return '';
  const name = item.name || item.title || '';
  const code = item.code || '';

  // Prefer code for protocol/room/PPE types (they use codes as identifiers)
  const codeTypes = [
    'protocol_categories', 'protocol_types', 'room_types', 'ppe_types', 'verification_methods',
    'trigger_events', 'frequency_types', 'location_types', 'process_categories'
  ];
  const lookupKey = codeTypes.includes(type) && code ? code : normalizeKey(name);

  if (!lookupKey) return name;

  const key = `nomenclatures:${type}.${lookupKey}`;
  return t(key, { defaultValue: name });
}

/**
 * Map tab id (kebab-case) to nomenclature type for translation lookup
 */
export function tabIdToNomenclatureType(tabId) {
  const map = {
    'protocol-categories': 'protocol_categories',
    'protocol-types': 'protocol_types',
    'room-types': 'room_types',
    'ppe-types': 'ppe_types',
    'verification-methods': 'verification_methods',
    'trigger-events': 'trigger_events',
    'frequency-types': 'frequency_types',
    'location-types': 'location_types',
    'action-types': 'action_types',
    'equipment': 'equipment',
    'supplies': 'supplies',
    'condition-types': 'condition_types',
    'documentation-field-types': 'documentation_field_types',
    'process-categories': 'process_categories'
  };
  return map[tabId] || tabId;
}

/**
 * Get the translation key for a nomenclature type label (e.g. "Task Types" -> "Tipuri sarcini")
 */
export function getNomenclatureTypeLabelKey(type) {
  const labelMap = {
    task_types: 'labels.taskTypes',
    task_statuses: 'labels.taskStatuses',
    task_priorities: 'labels.taskPriorities',
    task_urgencies: 'labels.taskUrgencies',
    assignment_types: 'labels.assignmentTypes',
    recurrence_frequencies: 'labels.recurrenceFrequencies',
    patient_conditions: 'labels.patientConditions',
    isolation_levels: 'labels.isolationLevels',
    equipment_types_task: 'labels.equipmentTypes',
    procedure_types: 'labels.procedureTypes',
    environmental_risks: 'labels.environmentalRisks',
    shift_types: 'labels.shiftTypes',
    completion_qualities: 'labels.completionQualities',
    deviation_types: 'labels.deviationTypes',
    risk_levels: 'labels.riskLevels',
    group_types: 'labels.groupTypes',
    workload_balancing_methods: 'labels.workloadBalancing',
    notification_channels: 'labels.notificationChannels',
    protocol_categories: 'labels.protocolCategories',
    protocol_types: 'labels.protocolTypes',
    room_types: 'labels.roomTypes',
    ppe_types: 'labels.ppeTypes',
    verification_methods: 'labels.verificationMethods',
    trigger_events: 'labels.triggerEvents',
    frequency_types: 'labels.frequencyTypes',
    location_types: 'labels.locationTypes',
    action_types: 'labels.actionTypes',
    equipment: 'labels.equipment',
    supplies: 'labels.supplies',
    condition_types: 'labels.conditionTypes',
    documentation_field_types: 'labels.documentationFieldTypes',
    process_categories: 'labels.processCategories'
  };
  return `nomenclatures:${labelMap[type] || type}`;
}

/**
 * Get the translation key for a protocol nomenclature tab label
 */
export function getProtocolTabLabelKey(tabId) {
  const labelMap = {
    'protocol-categories': 'labels.protocolCategories',
    'protocol-types': 'labels.protocolTypes',
    'room-types': 'labels.roomTypes',
    'ppe-types': 'labels.ppeTypes',
    'verification-methods': 'labels.verificationMethods',
    'trigger-events': 'labels.triggerEvents',
    'frequency-types': 'labels.frequencyTypes',
    'location-types': 'labels.locationTypes',
    'action-types': 'labels.actionTypes',
    'equipment': 'labels.equipment',
    'supplies': 'labels.supplies',
    'condition-types': 'labels.conditionTypes',
    'documentation-field-types': 'labels.documentationFieldTypes',
    'process-categories': 'labels.processCategories'
  };
  return `nomenclatures:${labelMap[tabId] || tabId}`;
}

export default translateNomenclature;
